'use client';

import React from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import TaggedSelect from '@/components/common/TaggedSelect';

interface ArticlesToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filterState: string | undefined;
  onFilterStateChange: (state: string | undefined) => void;
  availableCategories: Array<{ value: string; label: string }>;
  selectedCategory: string | undefined;
  onCategoryChange: (category: string | undefined) => void;
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  isLoading: boolean;
  statusFilter: 'all' | 'draft' | 'published';
  onStatusFilterChange: (status: 'all' | 'draft' | 'published') => void;
}

export default function ArticlesToolbar({
  searchQuery,
  onSearchChange,
  filterState,
  onFilterStateChange,
  availableCategories,
  selectedCategory,
  onCategoryChange,
  availableTags,
  selectedTags,
  onTagsChange,
  isLoading,
  statusFilter,
  onStatusFilterChange,
}: ArticlesToolbarProps) {
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = React.useState(false);
  const tabs = [
    { name: 'Active', value: 'active' },
    { name: 'Archived', value: 'archived' },
  ];

  // Convert available tags to TaggedSelectOption format
  const tagOptions = availableTags.map(tag => ({
    value: tag,
    label: tag
  }));

  // Handle tags change from TaggedSelect to ensure type safety
  const handleTagsChange = (value: string | string[]) => {
    const tags = Array.isArray(value) ? value : [];
    onTagsChange(tags);
  };

  const isDraftActive = statusFilter === 'all' || statusFilter === 'draft';
  const isPublishedActive = statusFilter === 'all' || statusFilter === 'published';

  const handleToggleStatus = (value: 'draft' | 'published') => {
    // Derive current boolean states from the enum-like statusFilter
    let nextDraft = isDraftActive;
    let nextPublished = isPublishedActive;

    if (value === 'draft') {
      nextDraft = !nextDraft;
    } else {
      nextPublished = !nextPublished;
    }

    // Ensure at least one status remains active
    if (!nextDraft && !nextPublished) {
      nextDraft = true;
      nextPublished = true;
    }

    let nextStatus: 'all' | 'draft' | 'published';
    if (nextDraft && nextPublished) {
      nextStatus = 'all';
    } else if (nextDraft) {
      nextStatus = 'draft';
    } else {
      nextStatus = 'published';
    }

    onStatusFilterChange(nextStatus);
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      {/* Top Row: Tabs + Mobile Filter Toggle */}
      <div className="flex items-center justify-between mb-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              onClick={() => onFilterStateChange(tab.value)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${filterState === tab.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
        {/* Mobile Toggle Button */}
        <button
          type="button"
          className="md:hidden p-2 text-gray-500 hover:text-blue-600 focus:outline-none"
          onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
        >
          <span className="sr-only">Toggle Filters</span>
          {isMobileFiltersOpen ? (
            <span className="text-sm font-medium">Hide Filters</span>
          ) : (
            <div className="flex items-center">
              <span className="text-sm font-medium mr-1">Filters</span>
              <MagnifyingGlassIcon className="h-5 w-5" />
            </div>
          )}
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        {/* Status Filter - Always visible if active tab */}
        {filterState === 'active' && (
          <div className="w-full md:w-56 flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <div className="inline-flex rounded-md shadow-sm border border-gray-300 bg-white" role="group">
              <button
                type="button"
                onClick={() => handleToggleStatus('draft')}
                className={`px-3 py-1 text-sm font-medium border-r border-gray-200
                  ${isDraftActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'}
                  rounded-l-md
                `}
              >
                Draft
              </button>
              <button
                type="button"
                onClick={() => handleToggleStatus('published')}
                className={`px-3 py-1 text-sm font-medium last:border-r-0
                  ${isPublishedActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'}
                  rounded-r-md
                `}
              >
                Published
              </button>
            </div>
          </div>
        )}

        {/* Collapsible Section for Mobile (Category, Tags, Search) */}
        <div className={`
            w-full flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:space-x-4 md:flex
            ${isMobileFiltersOpen ? 'flex' : 'hidden'}
        `}>
          {/* Category filter */}
          <div className="w-full md:w-48 flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <TaggedSelect
              value={selectedCategory || ''}
              onChange={(value) => {
                const category = typeof value === 'string' ? value : '';
                onCategoryChange(category || undefined);
              }}
              options={availableCategories}
              placeholder="All Categories"
              multiple={false}
              editable={false}
            />
          </div>

          {/* Tags filter */}
          <div className="w-full md:w-64 flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TaggedSelect
              value={selectedTags}
              onChange={handleTagsChange}
              options={tagOptions}
              placeholder="Filter by tags"
              multiple={true}
              editable={true}
            />
          </div>

          {/* Search box - takes all remaining space */}
          <div className="w-full flex-grow">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                name="search"
                id="search"
                className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
